import React, { createContext, useState, useContext, ReactNode } from 'react';
interface UploadProgressContextType {
  isUploading: boolean;
  uploadProgress: number;
  uploadFileName: string | null;
  uploadFileCount: number;
  currentFileIndex: number;
  startUpload: (fileName: string, fileCount?: number) => void;
  updateProgress: (progress: number, currentIndex?: number) => void;
  setCurrentFileName: (fileName: string) => void;
  finishUpload: () => void;
}
const UploadProgressContext = createContext<UploadProgressContextType | undefined>(undefined);
export const UploadProgressProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState<string | null>(null);
  const [uploadFileCount, setUploadFileCount] = useState(1);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const startUpload = (fileName: string, fileCount: number = 1) => {
    setIsUploading(true);
    setUploadProgress(0);
    setUploadFileName(fileName);
    setUploadFileCount(fileCount);
    setCurrentFileIndex(0);
  };
  const updateProgress = (progress: number, currentIndex: number = 0) => {
    setUploadProgress(Math.min(100, Math.max(0, progress)));
    setCurrentFileIndex(currentIndex);
  };
  const setCurrentFileName = (fileName: string) => {
    setUploadFileName(fileName);
  };
  const finishUpload = () => {
    setIsUploading(false);
    setUploadProgress(0);
    setUploadFileName(null);
    setUploadFileCount(1);
    setCurrentFileIndex(0);
  };
  return (
    <UploadProgressContext.Provider
      value={{
        isUploading,
        uploadProgress,
        uploadFileName,
        uploadFileCount,
        currentFileIndex,
        startUpload,
        updateProgress,
        setCurrentFileName,
        finishUpload
      }}
    >
      {children}
    </UploadProgressContext.Provider>
  );
};
export const useUploadProgress = () => {
  const context = useContext(UploadProgressContext);
  if (context === undefined) {
    throw new Error('useUploadProgress must be used within an UploadProgressProvider');
  }
  return context;
};